import React, { useEffect, useRef } from 'react';
import { cn } from './cn';
import { IconButton } from './Button';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  /** Responsive sizing — max-width classes. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Disable closing on backdrop click. */
  dismissible?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Adds a right-side close icon in the header. */
  showClose?: boolean;
}

const SIZE: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-3xl', xl: 'max-w-5xl',
};

export const Modal: React.FC<ModalProps> = ({
  open, onClose, title, description, size = 'md', dismissible = true,
  children, footer, showClose = true,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, dismissible]);

  if (!open) return null;

  return (
    <div
      // z-[60] sits above MobileTabBar (z-50). With both at z-50, DOM order
      // put the tab bar on top of the modal, hiding the footer's Submit/Cancel.
      className="fixed inset-0 z-[60] overflow-y-auto animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        className="fixed inset-0 bg-ink/40 backdrop-blur-[2px]"
        onClick={dismissible ? onClose : undefined}
      />
      {/* Centered on all viewports. Earlier we used items-end on mobile for
          an iOS-style bottom sheet, but with short content (e.g. the 4-field
          NewInspectionModal) the panel either sat awkwardly flush against
          the bottom edge or — once min-h was applied — ballooned into a
          tall sheet with empty space inside. Centering sizes to content and
          keeps the modal visually balanced on every device. */}
      <div className="flex min-h-full items-center justify-center p-4 pb-[calc(56px+env(safe-area-inset-bottom))]">
        <div
          ref={ref}
          className={cn(
            'relative w-full bg-surface-1 shadow-modal border border-border-subtle',
            // Uniform rounded-2xl matches the rounding cascade used by cards
            // elsewhere; was rounded-t-3xl on mobile + rounded-2xl on sm+
            // which made the bottom-sheet look disconnected from the rest.
            'rounded-2xl overflow-hidden',
            'animate-fade-up',
            // dvh tracks Safari's address-bar show/hide; vh would size against
            // the chrome-hidden value and overflow.
            'max-h-[92dvh] flex flex-col',
            SIZE[size],
          )}
        >
          {(title || showClose) && (
            <header className="flex items-start justify-between gap-4 px-5 md:px-6 pt-5 md:pt-6 pb-4 border-b border-border-subtle">
              <div className="min-w-0">
                {title && (
                  <h2 id="modal-title" className="text-heading-md text-ink">
                    {title}
                  </h2>
                )}
                {description && (
                  <p className="mt-1 text-body-sm text-ink-subtle">{description}</p>
                )}
              </div>
              {showClose && (
                <IconButton
                  label="Close"
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  icon={
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  }
                />
              )}
            </header>
          )}
          <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 md:py-6">{children}</div>
          {footer && (
            <footer className="px-5 md:px-6 py-4 border-t border-border-subtle flex items-center justify-end gap-3 bg-surface-0/50">
              {footer}
            </footer>
          )}
        </div>
      </div>
    </div>
  );
};
